
import { Tag , Image } from "antd";
import {
  firstLetterCaps,
  isoDateToDate,
  safeParse,
  trimToEllipsis,
} from "../../helpers/helper";
import { SettingOutlined, EditOutlined,UndoOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { FormNavigator } from "../../dynamicform/formNavigator";

import { useDispatch, useSelector } from "react-redux";
import DeleteWithPopup from "./DeleteWithPopup";
import resolveFromStore from "../../helpers/resolvers";
import { updateFormData } from "../../dynamicform/formInterface";
import { clearRentInfo , clearEventsInfo } from "../../features/rentSlice"
import { loadRentals } from "../../interfaces/rentInterface";
import { loadEvents } from "../../interfaces/eventInterface";


// ── SESSION CACHE ─────────────────────────────────────────────────────────────



const COLOR_PALETTE = [
  { strip: "linear-gradient(90deg,#2563eb,#000000)", avatar: "linear-gradient(135deg,#2563eb,#000000)" }, // blue → black
  { strip: "linear-gradient(90deg,#ea580c,#000000)", avatar: "linear-gradient(135deg,#ea580c,#000000)" }, // orange → black
  { strip: "linear-gradient(90deg,#dc2626,#000000)", avatar: "linear-gradient(135deg,#dc2626,#000000)" }, // red → black
  { strip: "linear-gradient(90deg,#000000,#1a1a1a)", avatar: "linear-gradient(135deg,#111111,#2a2a2a)" }, // pure black
  { strip: "linear-gradient(90deg,#6b0f1a,#000000)", avatar: "linear-gradient(135deg,#6b0f1a,#000000)" }, // maroon → black
  { strip: "linear-gradient(90deg,#16a34a,#000000)", avatar: "linear-gradient(135deg,#16a34a,#000000)" }, // green → black
  { strip: "linear-gradient(90deg,#9333ea,#000000)", avatar: "linear-gradient(135deg,#9333ea,#000000)" }, // purple → black
  { strip: "linear-gradient(90deg,#0891b2,#000000)", avatar: "linear-gradient(135deg,#0891b2,#000000)" }, // cyan → black
  { strip: "linear-gradient(90deg,#be185d,#000000)", avatar: "linear-gradient(135deg,#be185d,#000000)" }, // pink/rose → black
  { strip: "linear-gradient(90deg,#b45309,#000000)", avatar: "linear-gradient(135deg,#b45309,#000000)" }, // amber/gold → black
];
const SESSION_KEY = "type_colors";

let SESSION_COLORS = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
let colorCounter = Object.keys(SESSION_COLORS).length;

const getTypeColor = (type) => {
  const key = String(type ?? "default");

  if (!SESSION_COLORS[key]) {
    SESSION_COLORS[key] = COLOR_PALETTE[colorCounter % COLOR_PALETTE.length];
    colorCounter++;

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(SESSION_COLORS));
  }

  return SESSION_COLORS[key];
};

// ── CONFIG (unchanged) ────────────────────────────────────────────────────────
var configs = {
  rental: {
    title: "name",
    subTitle: "type",
    description: "description",
    link: (id) => `/rental/${id}`,
    edit: (formNavigator, id) => { formNavigator.rental(id); },
   getImage: (data) => {
  if (!data?.images) return null;

      const images = safeParse(data.images);
      const image = images?.[0];

      if (!image) return null;

      return (
        <Image
          src={image}
          width={"100%"}
          height={"100%"}
          style={{ objectFit: "cover", borderRadius: 6 }}
          preview={false}
        />
      );
    },
    recover:async(id , filters, dispatch)=>{
      await updateFormData("rental",id,"archived",false)
      dispatch(clearRentInfo());
      await loadRentals(dispatch,
            undefined,
            undefined,
            undefined,
            undefined,
            filters
          )
    }
  },
  event: {
    title: "name",
    subTitle: "startDate",
    description: "description",
    time: "endDate",
    link: (id, data) => data.isMultiTenant ? `/suite/${id}` : `/event/${id}`,
    edit: (formNavigator, id) => { formNavigator.event(id); },
    recover:async(id,filters,dispatch)=>{
      await updateFormData("event",id,"archived",false)
      dispatch(clearEventsInfo());
      await loadEvents(dispatch,
            undefined,
            undefined,
            undefined,
            filters
          )
    }
  },
};

// ── CUSTOM CARD ───────────────────────────────────────────────────────────────
const CustomCard = ({ config, data }) => {
  const dispatch      = useDispatch();
  const navigate      = useNavigate();
  const formNavigator = FormNavigator(dispatch);
  const rentalFilters = useSelector(state=>state.rent.rentalFilters)
  const eventFilters = useSelector(state=>state.rent.eventFilters)
  const cfg         = configs[config];
  const isDate      = cfg.subTitle.endsWith("Date");
  const subTitleRaw = cfg.subTitle in resolveFromStore
    ? resolveFromStore[cfg.subTitle](data[cfg.subTitle])
    : data[cfg.subTitle];
  const typeKey   = isDate ? "date" : String(subTitleRaw ?? "default");
  const typeLabel = isDate ? isoDateToDate(data[cfg.subTitle], false) : firstLetterCaps(subTitleRaw);
  const color     = getTypeColor(typeKey);
  const initial   = config === "event" ? "E" : typeKey.charAt(0).toUpperCase();

  return (
    <div className="group relative flex flex-col bg-white rounded-2xl border border-[#eef1f8] shadow-sm hover:-translate-y-1 hover:shadow-xl hover:border-[#d4d8f0] transition-all duration-200 overflow-visible cursor-pointer">

      <Link to={cfg.link(data["id"], data)} className="block no-underline text-inherit">

        {/* ── COLOR STRIP ── */}
        <div
          className="h-[52px] rounded-t-2xl relative flex-shrink-0"
          style={{ background: color.strip }}
        >
          {/* For non-event: type tag top right */}

            <span className="absolute top-2.5 right-3 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wide text-white"
              style={{ background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.3)", backdropFilter: "blur(6px)" }}>
              {config == 'event' ? 'Event' :typeLabel}
            </span>

        </div>

        {/* ── FLOATING AVATAR ── */}
        {config !== 'event' && <div
          className="absolute top-[0px] left-0 w-[130px] h-[80px] rounded-[14px] flex items-center justify-center text-xl font-extrabold text-white z-10 border-[3px]  group-hover:scale-110 transition-transform duration-200"
          style={{ background: color.avatar, boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}
        >
          { cfg.getImage ? (cfg.getImage(data) || initial) : initial}
        </div>}

        {/* ── BODY ── */}
        <div className={config == 'event' ? "pt-3 px-3.5 pb-2":"pt-8 px-3.5 pb-2"}>

          {/* Title */}
          <div className="text-[14.5px] font-bold text-slate-900 mb-2 leading-snug truncate">
            {data[cfg.title]}
          </div>

          {/* Event: date range */}
          {config === "event" && (
            <div className="flex items-center gap-2 mb-2.5">
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1.5 text-[11px] text-slate-500 font-medium">
                <span className="text-slate-400">📅</span>
                <span className="font-semibold text-slate-600">From</span>
                <span>{isoDateToDate(data[cfg.subTitle], false)}</span>
              </div>
              {data[cfg.time] && (
                <>
                  <span className="text-slate-300 text-xs">→</span>
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1.5 text-[11px] text-slate-500 font-medium">
                    <span className="font-semibold text-slate-600">To</span>
                    <span>{isoDateToDate(data[cfg.time], false)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Description */}
          {data[cfg.description] && (
            <div className="text-[12px] text-slate-400 leading-relaxed break-words overflow-y-auto"
              style={{ maxHeight: 150 }}>
              {trimToEllipsis(data[cfg.description])}
            </div>
          )}

        </div>
      </Link>

{/* ── ACTION BUTTONS ── */}
<div className="px-4 py-3 flex items-center gap-2">

  {/* Edit — grows, labeled */}
  {!data.archived && <button
    className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-900 hover:text-white transition-all duration-150 border-none cursor-pointer group"
    onClick={(e) => { configs[config].edit(formNavigator, data["id"]); e.stopPropagation(); }}
    title="Edit"
  >
    <EditOutlined className="group-hover:-rotate-6 transition-transform duration-200" />
    <span>Edit</span>
  </button>}

  {/* Settings — icon only, spins on hover */}
  <button
    className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 hover:rotate-45 transition-all duration-200 border-none cursor-pointer"
    onClick={() => navigate(configs[config].link(data["id"]))}
    title="Settings"
  >
    <SettingOutlined />
  </button>

  {/* Delete or Recover based on archived status */}
  {data.archived ? (
    <button
      className="w-30 h-9 p-2 flex items-center justify-center rounded-xl text-slate-500 bg-slate-100 hover:bg-red-50 hover:text-red-500 transition-all duration-150 border-none cursor-pointer"
      title="Recover"
      onClick={async(e) =>{ configs[config].recover(data['id'],config=='event'?eventFilters:rentalFilters,dispatch) } }
    >
      <UndoOutlined /> {" Recover"}
    </button>
  ) : (
    <button
      className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 bg-slate-100 hover:bg-red-50 hover:text-red-500 transition-all duration-150 border-none cursor-pointer"
      title="Delete"

    >
      <DeleteWithPopup modal={config} id={data["id"]}  />
    </button>
  )}

</div>

    </div>
  );
};

export default CustomCard;
